import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import moment from 'moment';
import { filter } from 'rxjs/operators';
import { ToastrService } from 'ngx-toastr';
import {
    extractNotificationEventId,
    extractNotificationEventName,
} from 'src/app/shared/utils/notification-alert.helpers';
import { SocketService } from './socket.service';
import { VendorCounterOfferEventNameService } from './vendor-counter-offer-event-name.service';

/**
 * App-wide warning toast when admin extends an event end time (`EVENT_EXTENDED` on `event_update`).
 */
@Injectable({ providedIn: 'root' })
export class VendorEventExtendedSocketToastService {
    private readonly socketService = inject(SocketService);
    private readonly toastr = inject(ToastrService);
    private readonly eventNameService = inject(VendorCounterOfferEventNameService);
    private readonly destroyRef = inject(DestroyRef);
    private readonly recentToastKeys = new Map<string, number>();

    constructor() {
        this.socketService.socketEventData
            .pipe(filter((d: any) => d != null), takeUntilDestroyed(this.destroyRef))
            .subscribe((data) => this.handleEventPayload(data));
    }

    private handleEventPayload(socketPayload: any): void {
        const type = String(socketPayload?.type ?? socketPayload?.data?.type ?? '').toUpperCase();
        if (type !== 'EVENT_EXTENDED') {
            return;
        }

        const inner = socketPayload?.data ?? socketPayload;
        const originalEndDate = inner?.originalEndDate ?? socketPayload?.originalEndDate;
        const newEndDate = inner?.newEndDate ?? socketPayload?.newEndDate;
        const durationText = this.formatExtensionDuration(originalEndDate, newEndDate);
        if (!durationText) {
            return;
        }

        const eventId = extractNotificationEventId(socketPayload) ?? String(socketPayload?.eventId ?? '').trim();
        let eventName = extractNotificationEventName(socketPayload);
        if (!eventName && eventId) {
            eventName = this.eventNameService.lookup(eventId);
        }
        if (!eventName) {
            eventName = this.eventNameService.lookupRecent();
        }
        const eventLabel = String(eventName ?? '').trim() || 'event';

        const message = `This event ${eventLabel} is extended by ${durationText}.`;
        const dedupeKey = `EXT|${eventId}|${originalEndDate}|${newEndDate}`;
        this.showWarningToast('Event Extended', message, dedupeKey);
    }

    private formatExtensionDuration(originalEndDate: string, newEndDate: string): string {
        const start = moment(originalEndDate, moment.ISO_8601 as any, true);
        const end = moment(newEndDate, moment.ISO_8601 as any, true);
        if (!start.isValid() || !end.isValid() || !end.isAfter(start)) {
            return '';
        }

        const duration = moment.duration(end.diff(start));
        const totalMinutes = Math.floor(duration.asMinutes());
        const days = Math.floor(totalMinutes / (24 * 60));
        const hours = Math.floor((totalMinutes % (24 * 60)) / 60);
        const minutes = totalMinutes % 60;
        const seconds = duration.seconds();

        const parts: string[] = [];
        if (days > 0) {
            parts.push(`${days} day${days === 1 ? '' : 's'}`);
        }
        if (hours > 0) {
            parts.push(`${hours} hr${hours === 1 ? '' : 's'}`);
        }
        if (minutes > 0) {
            parts.push(`${minutes} min`);
        }
        if (parts.length === 0 && seconds > 0) {
            parts.push(`${seconds} sec`);
        }
        return parts.join(' ');
    }

    private showWarningToast(title: string, message: string, dedupeKey: string): void {
        const text = String(message ?? '').trim();
        if (!text) {
            return;
        }
        if (this.isRecentDuplicate(dedupeKey)) {
            return;
        }
        this.toastr.warning(text, title);
    }

    private isRecentDuplicate(key: string): boolean {
        const now = Date.now();
        const ttlMs = 15000;
        const last = this.recentToastKeys.get(key);
        if (last != null && now - last < ttlMs) {
            return true;
        }
        this.recentToastKeys.set(key, now);
        return false;
    }
}
